import { useCallback, useRef, useState } from 'react';
import { atom, useAtom } from 'jotai';
import { api } from 'lib/request/api';
import { apiErrorMessage } from 'utils/api-errors';
import { buildReconcileBullets } from 'pages/entities/show/reconcile/reconcile-bullets';
import { CANDIDATES_NOT_ASSEMBLED_MESSAGE } from 'pages/entities/show/reconcile/utils';
import {
  fetchScoredCandidates,
  newReconcileSessionId,
  undoReconciliation,
} from 'pages/entities/show/reconcile/api';
import type {
  EntityResponse,
  EntityReconcileCandidate,
  EntityReconcileScoredResponse,
  EntityReconcileCreateResponse,
} from 'types/api-responses';

// Post-reconcile summary for the list context: plain-language bullets plus what
// Undo needs — the reconciliation id scoped under the launched-from entity and
// the session token the reconcile was CREATED under.
export interface ListReconcileSummary {
  result: EntityReconcileCreateResponse;
  fromEntityId: number;
  bullets: string[];
  sessionId: string;
  // Add-as-New conversions committed during this reconcile session (§3.14). Each
  // is its own transaction and is NOT reversed by Undo, so the confirmation must
  // disclose that they remain in effect when the count is non-zero — mirroring
  // the profile surface.
  addAsNewCount: number;
}

// Atoms (not hook state) so the page header — a different component tree — can
// render the "Undo Reconcile" button per the mock (top right, next to Create
// New Entity) while the dashboard content owns the modal + API flow.
export const listReconcileSummaryAtom = atom<ListReconcileSummary | null>(null);
export const listUndoModalOpenAtom = atom(false);

export interface EntityListReconcile {
  assembling: boolean;
  launchFor: (entityId: number) => void;
  launchError: string | null;
  dismissLaunchError: () => void;
  sessionId: string;
  tearsheetOpen: boolean;
  viewedEntity: EntityResponse | null;
  candidates: EntityReconcileCandidate[];
  // The viewed entity's own alias-family breakdown, for its right-panel row.
  viewedAliasGroup: EntityReconcileScoredResponse['viewedAliasGroup'];
  moreFound: boolean;
  totalBeforeCap: number;
  // Diagnostics only (R2): the mode the server actually used (reflects automatic
  // lexical fallback) and whether the semantic capability is enabled here. No UI
  // branches on these — the mode is server-decided, with no user-facing toggle.
  semantic: boolean | null;
  semanticAvailable: boolean;
  closeTearsheet: () => void;
  notchCanonize: () => void;
  complete: (
    result: EntityReconcileCreateResponse,
    namesById: Record<number, string>
  ) => void;
  allEliminatedOpen: boolean;
  closeAllEliminated: () => void;
  summary: ListReconcileSummary | null;
  dismissSummary: () => void;
  undoModalOpen: boolean;
  cancelUndo: () => void;
  confirmUndo: () => void;
  undoing: boolean;
  undoError: string | null;
}

// Drives the list-page inline Reconcile action (mock: entity-list-page-
// reconciliation): no per-row candidate retrieval — the icon is an unconditional
// shortcut, and everything (full entity + scored candidates) is fetched on
// click, matching the narrative's decision to keep candidate retrieval off
// search-result lists.
export function useEntityListReconcile(
  onReconciled: () => void
): EntityListReconcile {
  const [assembling, setAssembling] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [viewedEntity, setViewedEntity] = useState<EntityResponse | null>(null);
  const [candidates, setCandidates] = useState<EntityReconcileCandidate[]>([]);
  const [viewedAliasGroup, setViewedAliasGroup] =
    useState<EntityReconcileScoredResponse['viewedAliasGroup']>(null);
  const [moreFound, setMoreFound] = useState(false);
  const [totalBeforeCap, setTotalBeforeCap] = useState(0);
  // Server-decided retrieval mode + capability, captured for diagnostics only.
  const [semantic, setSemantic] = useState<boolean | null>(null);
  const [semanticAvailable, setSemanticAvailable] = useState(false);
  const [tearsheetOpen, setTearsheetOpen] = useState(false);
  const [allEliminatedOpen, setAllEliminatedOpen] = useState(false);
  const [summary, setSummary] = useAtom(listReconcileSummaryAtom);
  const [undoModalOpen, setUndoModalOpen] = useAtom(listUndoModalOpenAtom);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  // One working-session token per list visit — scopes the in-session undo stack
  // (LIFO: the banner always reflects the latest reconcile, which is the only
  // undoable one).
  const [sessionId] = useState(newReconcileSessionId);

  // A monotonic launch token so a slow resolution can't land after a newer
  // launch for a different row superseded it (the list-page analogue of
  // use-entity-reconcile's entityIdRef guard).
  const launchTokenRef = useRef(0);

  // Add-as-New conversions this session (§3.14). A ref, not state — it only needs
  // to be read at submit time, and bumping it must not re-render the tearsheet.
  const canonizeCountRef = useRef(0);
  const notchCanonize = useCallback(() => {
    canonizeCountRef.current += 1;
  }, []);

  // Row-action click: fetch the full entity (the tearsheet's anchor row needs
  // addresses/phones) and the scored candidate set together, then open. A fetch
  // FAILURE is an error state, never Case 4 — the all-eliminated modal is a
  // scoring verdict the backend didn't render.
  const launchFor = useCallback((entityId: number) => {
    setAssembling(true);
    setLaunchError(null);
    // Fresh session: a new set of Add-as-New conversions starts from zero.
    canonizeCountRef.current = 0;
    const token = ++launchTokenRef.current;
    Promise.all([
      api.get(`entities/${entityId}`).json<EntityResponse>(),
      fetchScoredCandidates(entityId),
    ])
      .then(([entity, scored]) => {
        if (launchTokenRef.current !== token) return; // superseded — stale
        // Record the mode the server used (semantic vs its lexical fallback) and
        // the environment capability — diagnostics only; nothing branches on it.
        setSemantic(scored.semantic ?? null);
        setSemanticAvailable(!!scored.semanticAvailable);
        if (scored.allEliminated || !scored.candidates?.length) {
          setAllEliminatedOpen(true);
          return;
        }
        setViewedEntity(entity);
        setCandidates(scored.candidates);
        setViewedAliasGroup(scored.viewedAliasGroup ?? null);
        setMoreFound(!!scored.moreFound);
        setTotalBeforeCap(scored.totalBeforeCap ?? scored.candidates.length);
        setTearsheetOpen(true);
      })
      .catch(() => {
        if (launchTokenRef.current !== token) return; // superseded — stale
        setLaunchError(CANDIDATES_NOT_ASSEMBLED_MESSAGE);
      })
      .finally(() => {
        if (launchTokenRef.current !== token) return; // a newer launch owns it
        setAssembling(false);
      });
  }, []);

  const complete = useCallback(
    (
      result: EntityReconcileCreateResponse,
      namesById: Record<number, string>
    ) => {
      setTearsheetOpen(false);
      if (viewedEntity) {
        setSummary({
          result,
          fromEntityId: viewedEntity.id,
          bullets: buildReconcileBullets(result, namesById),
          sessionId,
          addAsNewCount: canonizeCountRef.current,
        });
      }
      // Stay on the list (no profile navigation) — refresh so retired entities
      // drop out / show their Deduplicated badge.
      onReconciled();
    },
    [viewedEntity, onReconciled, sessionId, setSummary]
  );

  const confirmUndo = useCallback(() => {
    if (!summary) return;
    setUndoing(true);
    setUndoError(null);
    undoReconciliation(
      summary.fromEntityId,
      summary.result.reconciliationId,
      summary.sessionId
    )
      .then(() => {
        setSummary(null);
        setUndoModalOpen(false);
        // The undo endpoint reindexes the restored entities synchronously, so
        // this refetch shows them back in the list immediately.
        onReconciled();
      })
      .catch(async (err) => {
        // Surface the server's reason (contention, out-of-session, …) in the
        // modal; the examiner can retry or cancel.
        setUndoError(await apiErrorMessage(err, 'Undo failed — try again.'));
      })
      .finally(() => setUndoing(false));
  }, [summary, onReconciled, setSummary, setUndoModalOpen]);

  return {
    assembling,
    launchFor,
    launchError,
    dismissLaunchError: () => setLaunchError(null),
    sessionId,
    tearsheetOpen,
    viewedEntity,
    candidates,
    viewedAliasGroup,
    moreFound,
    totalBeforeCap,
    semantic,
    semanticAvailable,
    closeTearsheet: () => setTearsheetOpen(false),
    notchCanonize,
    complete,
    allEliminatedOpen,
    closeAllEliminated: () => setAllEliminatedOpen(false),
    summary,
    dismissSummary: () => setSummary(null),
    undoModalOpen,
    cancelUndo: () => {
      setUndoError(null);
      setUndoModalOpen(false);
    },
    confirmUndo,
    undoing,
    undoError,
  };
}
